/** @jsxImportSource @emotion/react */
import css, { SerializedStyles } from '@emotion/css/macro'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ProvinceSelectInput, Province } from '../ProvinceSelectInput'
import { useCountries, Country } from '@sportninja/common/hooks/useCountries'
import { CountrySelectInput } from '../CountrySelectInput'

/**
 * CountryAndProvinceInput component that displays country and province/state selectors side by side
 * Automatically handles province fetching based on selected country and switches to text input when no provinces exist
 * Works with both iso_3166_2 codes and country names
 * @component
 */
type CountryAndProvinceInputProps = {
  /** Current country value (iso_3166_2 code or country name, depending on valueMode) */
  countryValue: string | undefined
  /** Current province value (id, name, or text) */
  provinceValue: string | undefined
  /** Function to update country */
  onCountryChange: (value: string | undefined) => void
  /** Function to update province */
  onProvinceChange: (value: string | undefined) => void
  /** Value mode - 'iso_code' uses iso_3166_2, 'name' uses country name, 'id' uses numeric ID (default: 'iso_code') */
  valueMode?: 'iso_code' | 'name' | 'id'
  /** Province value mode - 'id' uses numeric ID, 'name' uses province name (default: 'id') */
  provinceValueMode?: 'id' | 'name' | 'iso_code'
  /** Field errors object */
  fieldErrors?: {
    country?: boolean | string
    province?: boolean | string
    state?: boolean | string
  }
  /** Custom error messages */
  customErrors?: {
    country?: string
    province?: string
  }
  /** Required fields configuration */
  requiredFields?: {
    country?: boolean
    province?: boolean
  }
  /** Labels for the fields */
  labels?: {
    country?: string
    province?: string
  }
  /** Placeholder texts */
  placeholders?: {
    country?: string
    province?: string
  }
  /** Whether to disable the inputs */
  disabled?: boolean
  /** Custom styling for the container */
  containerStyle?: SerializedStyles
  /** Responsive layout flag - if true, stacks vertically on mobile */
  responsive?: boolean
  /** Tab index for country field */
  countryTabIndex?: number
  /** Tab index for province field */
  provinceTabIndex?: number
  /** Whether to allow clearing selections */
  allowClear?: boolean
  /** Custom list of countries to use instead of fetching */
  customCountryList?: Country[]
  /** Ref to provinces to be used to store fetched provinces */
  provincesRef?: React.MutableRefObject<Province[]>
  /** Ref to countries to be used to store fetched countries */
  countriesRef?: React.MutableRefObject<Country[]>
}

export const CountryAndProvinceInput: React.FC<
  CountryAndProvinceInputProps
> = ({
  countryValue,
  provinceValue,
  onCountryChange,
  onProvinceChange,
  valueMode = 'iso_code',
  provinceValueMode = 'name',
  fieldErrors = {},
  customErrors = {},
  labels = {},
  placeholders = {},
  disabled = false,
  containerStyle,
  responsive = false,
  countryTabIndex,
  provinceTabIndex,
  allowClear = false,
  customCountryList,
  requiredFields = {},
  provincesRef,
  countriesRef,
}) => {
  console.log('🗺️ CountryAndProvinceInput: rendering component', {
    countryValue,
    provinceValue,
    valueMode,
    provinceValueMode,
  })

  const { countries: fetchedCountries, refetch } = useCountries()
  const countries = customCountryList || fetchedCountries

  // Track fetched provinces for value conversion
  const [provinces, setProvinces] = useState<Province[]>([])

  // Fetch countries on mount if not already loaded and not using custom list
  useEffect(() => {
    if (!customCountryList && countries.length === 0) {
      refetch()
    }
  }, [customCountryList, countries.length])

  useEffect(() => {
    if (
      countriesRef &&
      countries.length > 0 &&
      countriesRef.current.length === 0
    ) {
      countriesRef.current = countries
    }
  }, [countries, countriesRef])

  // Map country value to numeric ID for province fetching
  const countryId = useMemo(() => {
    console.log('🗺️ CountryAndProvinceInput: mapping country value to ID', {
      countryValue,
      countries,
      valueMode,
    })

    if (!countryValue || !countries.length) {
      return undefined
    }

    let country: Country | undefined

    if (valueMode === 'id') {
      // Value is already a numeric ID
      const numericId =
        typeof countryValue === 'string'
          ? parseInt(countryValue, 10)
          : countryValue
      console.log(
        '🗺️ CountryAndProvinceInput: country value is already ID',
        numericId
      )
      return numericId
    } else if (valueMode === 'iso_code') {
      // Value is iso_3166_2 code
      country = countries.find((c) => c.iso_3166_2 === countryValue)
    } else {
      // Value is country name
      country = countries.find(
        (c) =>
          c.name.toLowerCase() === countryValue.toLowerCase() ||
          c.name_full.toLowerCase() === countryValue.toLowerCase()
      )
    }

    if (country) {
      console.log(
        '🗺️ CountryAndProvinceInput: mapped country value',
        countryValue,
        'to ID',
        country.id
      )
      return country.id
    }

    console.warn(
      '🗺️ CountryAndProvinceInput: could not find country ID for value',
      countryValue
    )
    return undefined
  }, [countryValue, countries, valueMode])

  // Convert between value modes for CountrySelectInput
  const countryValueForInput = useMemo(() => {
    if (!countryValue || !countries.length) {
      return undefined
    }

    if (valueMode === 'iso_code') {
      // Input expects iso_3166_2, and that's what we have
      return countryValue
    } else if (valueMode === 'id') {
      // Value is numeric ID, need to convert to iso_3166_2 for the input
      const numericId =
        typeof countryValue === 'string'
          ? parseInt(countryValue, 10)
          : countryValue
      const country = countries.find((c) => c.id === numericId)
      console.log(
        '🗺️ CountryAndProvinceInput: converting ID',
        numericId,
        'to iso_code',
        country?.iso_3166_2
      )
      return country?.iso_3166_2
    } else {
      // Value is name, need to convert to iso_3166_2 for the input
      const country = countries.find(
        (c) =>
          c.name.toLowerCase() === countryValue.toLowerCase() ||
          c.name_full.toLowerCase() === countryValue.toLowerCase()
      )
      return country?.iso_3166_2
    }
  }, [countryValue, countries, valueMode])

  // Convert province value based on provinceValueMode
  const provinceValueForInput = useMemo(() => {
    if (!provinceValue || !provinces.length) {
      // If no provinces loaded, pass value as-is (for text input mode)
      return provinceValue
    }

    console.log(
      '🗺️ CountryAndProvinceInput: converting province value',
      provinceValue,
      'from mode',
      provinceValueMode,
      'to ID'
    )

    let province: Province | undefined

    if (provinceValueMode === 'id') {
      // Value is already an ID, pass as-is
      return provinceValue
    } else if (provinceValueMode === 'name') {
      // Value is a name, find the ID
      province = provinces.find(
        (p) => p.name.toLowerCase() === provinceValue.toLowerCase()
      )
    } else if (provinceValueMode === 'iso_code') {
      // Value is iso_3166_2 code, find the ID
      province = provinces.find((p) => p.iso_3166_2 === provinceValue)
    }

    if (province) {
      console.log(
        '🗺️ CountryAndProvinceInput: mapped province value',
        provinceValue,
        'to ID',
        province.id
      )
      return province.id.toString()
    }

    console.warn(
      '🗺️ CountryAndProvinceInput: could not find province for value',
      provinceValue
    )
    return undefined
  }, [provinceValue, provinces, provinceValueMode])

  // Track previous countryId to detect actual changes
  const prevCountryIdRef = useRef(countryId)

  // Clear province when country changes
  useEffect(() => {
    // Only clear if countryId actually changed (not on initial mount or province updates)
    if (
      prevCountryIdRef.current !== undefined &&
      prevCountryIdRef.current !== countryId
    ) {
      console.log(
        '🗺️ CountryAndProvinceInput: country changed, clearing province'
      )
      onProvinceChange(undefined)
    }
    prevCountryIdRef.current = countryId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId])

  const handleCountryChange = (isoCode: string | undefined) => {
    console.log('🗺️ CountryAndProvinceInput: country changed to', isoCode)

    if (!isoCode) {
      onCountryChange(undefined)
      return
    }

    if (valueMode === 'iso_code') {
      // Pass iso code directly
      onCountryChange(isoCode)
    } else if (valueMode === 'id') {
      // Convert iso code to numeric ID
      const country = countries.find((c) => c.iso_3166_2 === isoCode)
      if (country) {
        console.log(
          '🗺️ CountryAndProvinceInput: converting iso_code',
          isoCode,
          'to ID',
          country.id
        )
        onCountryChange(country.id.toString())
      }
    } else {
      // Convert iso code to country name
      const country = countries.find((c) => c.iso_3166_2 === isoCode)
      if (country) {
        onCountryChange(country.name)
      }
    }
  }

  const handleProvinceChange = (value: string | undefined) => {
    console.log('🗺️ CountryAndProvinceInput: province changed to', value)

    // If no value or no provinces (text input mode), pass value as-is
    if (!value || !provinces.length) {
      onProvinceChange(value)
      return
    }

    // Convert from ID back to the desired format
    if (provinceValueMode === 'id') {
      // Pass ID as-is
      onProvinceChange(value)
    } else {
      // Find the province by ID and convert to name or iso_code
      const province = provinces.find((p) => p.id.toString() === value)
      if (province) {
        if (provinceValueMode === 'name') {
          console.log(
            '🗺️ CountryAndProvinceInput: converting ID',
            value,
            'to name',
            province.name
          )
          onProvinceChange(province.name)
        } else if (provinceValueMode === 'iso_code') {
          console.log(
            '🗺️ CountryAndProvinceInput: converting ID',
            value,
            'to iso_code',
            province.iso_3166_2
          )
          onProvinceChange(province.iso_3166_2)
        }
      } else {
        console.warn(
          '🗺️ CountryAndProvinceInput: could not find province with ID',
          value
        )
        onProvinceChange(undefined)
      }
    }
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 8px;

        ${responsive &&
        `
          @media (max-width: 768px) {
            flex-direction: column;

            > div {
              width: 100%;
            }
          }
        `}
        ${containerStyle}
      `}
    >
      <div
        css={css`
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <CountrySelectInput
          name='country'
          value={countryValueForInput}
          onChange={handleCountryChange}
          fieldErrors={{
            country: fieldErrors?.country,
          }}
          customError={{
            country: customErrors?.country,
          }}
          label={labels?.country || 'Country'}
          placeholder={placeholders?.country || 'Select a Country'}
          disabled={disabled}
          allowClear={allowClear}
          tabIndex={countryTabIndex}
          required={requiredFields?.country}
        />
      </div>
      <div
        css={css`
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <ProvinceSelectInput
          name='province'
          value={provinceValueForInput}
          onChange={handleProvinceChange}
          countryId={countryId}
          fieldErrors={{
            province: fieldErrors?.province || fieldErrors?.state,
          }}
          customError={{
            province: customErrors?.province,
          }}
          label={labels?.province || 'Province/State'}
          placeholder={placeholders?.province || 'Select a Province/State'}
          disabled={disabled}
          allowClear={allowClear}
          tabIndex={provinceTabIndex}
          onProvincesLoaded={setProvinces}
          required={requiredFields?.province}
          provincesRef={provincesRef}
        />
      </div>
    </div>
  )
}

export default CountryAndProvinceInput
